// V97.4 — Couche clinicalContext (Phase V1).
// Date : 2026-05-12
//
// Rôle : transformer un objet `clinicalContext` structuré en bloc texte
// brut injectable dans le system prompt nutrition. Pendant naturel de
// _detector.fr.js (qui répond "qui est la cliente" depuis l'anamnèse),
// cette couche répond "ce qu'on a mesuré" depuis les tests + résultats +
// signaux + phase microbiome.
//
// Séparation des préoccupations :
//   _detector.fr.js       → form        → profils cliente (tags)
//   _clinicalContext.fr.js → clinicalContext → bloc clinique structuré
//
// V1 : prépare seulement l'API d'injection. La construction du clinicalContext
// depuis le SaaS (JourneyPlanEditor + analyses prescrites + résultats saisis)
// viendra en V2.
//
// Garanties :
// - Ne throw JAMAIS pour un clinicalContext partiel / null / undefined.
// - Retourne '' (empty string) si rien à injecter.
// - Tolère plusieurs conventions de noms (test.name OU test.display_name, etc.)
//   pour compatibilité avec différentes sources (catalogue Ortho/MGD, résultats
//   saisis manuellement, etc.).

import { ROUTE_PROGRAMME } from '../../../../server/mgd/mgd-clinical-routes.js';
import { resolveMaternalStateExtended } from './profiles/_detector.fr.js';
import { formatActiveTreatments } from '../../clinical/currentTreatments.js';
import { resolveRiskProfileFlags } from '../../riskProfileFlags.js';
// V97.64 : contraintes maternelles (P0) sorties dans une source unique
// DB-éditable. buildSafetyBlockFr lit l'entrée FR active (DB si préchargée,
// sinon hardcode validé). Voir src/services/clinicalMaternalSafety.js.
import { getActiveMaternalSafetyEntry } from '../../clinicalMaternalSafety.js';
import { resolveRestrictions, hasRestrictions } from './restrictions.fr.js';
import { formatComplementsActuels, resolveOuvertureComplements, OUVERTURE_COMPLEMENTS, isRamadanActive } from './anamneseFoundation.js';

/**
 * Détermine si un clinicalContext est vide (rien à injecter).
 * @param {object | null | undefined} ctx
 * @returns {boolean}
 */
export function isEmptyClinicalContext(ctx) {
  if (!ctx || typeof ctx !== 'object') return true;
  const hasTests = Array.isArray(ctx.selectedTests) && ctx.selectedTests.length > 0;
  const hasMarkers = Array.isArray(ctx.expectedMarkers) && ctx.expectedMarkers.length > 0;
  const hasResults = Array.isArray(ctx.enteredResults) && ctx.enteredResults.length > 0;
  const hasSignals = Array.isArray(ctx.clinicalSignals) && ctx.clinicalSignals.length > 0;
  const hasStage = ctx.microbiomeStage && typeof ctx.microbiomeStage === 'object';
  const hasModules = Array.isArray(ctx.promptModules) && ctx.promptModules.length > 0;
  const hasSafety = Array.isArray(ctx.safetyRules) && ctx.safetyRules.length > 0;
  // V97.177 : jeux de résultats MGD validés par Anissa (module séparé).
  const hasValidated = Array.isArray(ctx.validatedLabResults) && ctx.validatedLabResults.length > 0;
  return !(hasTests || hasMarkers || hasResults || hasSignals || hasStage || hasModules || hasSafety || hasValidated);
}

// ─── RÉSULTATS DE LABORATOIRE VALIDÉS (V97.177) ─────────────────────────
//
// Module STRUCTURÉ SÉPARÉ, distinct de `enteredResults` (saisie libre étape 4).
// Source : RPC lister_resultats_mgd_valides, alimentée par les validations
// V97.176. Ne contiennent que des valeurs qu'Anissa a explicitement acceptées
// ou corrigées — les valeurs refusées ne sont pas stockées, seul leur nombre
// l'est, et il est affiché ici comme indicateur de complétude.
//
// Aucune interprétation n'est produite ici : marqueur, valeur, unité, et la
// mention de qui a corrigé quoi. La version validée et sa date sont écrites
// dans le bloc pour que la proposition IA soit traçable à une validation
// précise, jamais à « les résultats » en général.

const DECISION_LABEL_FR = Object.freeze({
  accepted: 'valeur acceptée telle quelle',
  corrected: 'valeur corrigée par Anissa',
});

/**
 * Formate une valeur validée : "- Marqueur : valeur unité (décision)".
 * Fail-safe : renvoie '' si la ligne est inexploitable.
 */
export function formatValidatedResult(result) {
  if (!result || typeof result !== 'object') return '';
  const marker = String(result.markerName ?? '').trim();
  const value = String(result.finalValue ?? '').trim();
  if (!marker || !value) return '';
  const unit = String(result.unit ?? '').trim();
  const decision = DECISION_LABEL_FR[result.professionalDecision];
  let line = `- ${marker} : ${unit ? `${value} ${unit}` : value}`;
  if (decision) line += ` (${decision})`;
  return line;
}

/**
 * Formate un jeu de résultats validés, en-tête de traçabilité comprise.
 * Fail-safe : renvoie '' si aucune valeur exploitable.
 */
/**
 * Ne garde que ce qu'Anissa a explicitement destiné au programme.
 *
 * ⚠ FERMÉ PAR DÉFAUT. Tout ce qui n'est pas `programme` est écarté : le
 * contexte, l'avis médical, et surtout tout résultat dont la destination
 * manque. Une valeur sans destination n'est pas une valeur « neutre » — c'est
 * une valeur qu'Anissa n'a pas encore tranchée, et elle n'a rien à faire dans
 * un plan alimentaire.
 *
 * ⚠ CE FILTRE EST LE DERNIER MAILLON. Sans lui, les 231 lignes d'un rapport
 * ADN partaient EN ENTIER dans le contexte du compositeur, y compris les 225
 * restées « à vérifier ». Le classement était écrit, lu, et sans effet.
 */
export function resultatsDestinesAuProgramme(results) {
  const liste = Array.isArray(results) ? results : [];
  return liste.filter((resultat) => resultat?.clinicalRoute === ROUTE_PROGRAMME);
}

export function formatValidatedResultSet(set) {
  if (!set || typeof set !== 'object') return '';
  const results = Array.isArray(set.results) ? set.results : [];
  const retenus = resultatsDestinesAuProgramme(results);
  // Ce qu'Anissa a rangé ailleurs est COMPTÉ, jamais tu. Le compositeur doit
  // savoir qu'il ne voit pas tout, sinon il raisonne comme s'il avait tout.
  const ecartesParDestination = results.length - retenus.length;
  const lines = retenus.map(formatValidatedResult).filter(Boolean);
  if (lines.length === 0) return '';

  const version = Number.isFinite(Number(set.resultVersion)) ? `version ${set.resultVersion}` : 'version inconnue';
  let validated = '';
  const parsed = set.validatedAt ? new Date(set.validatedAt) : null;
  if (parsed && !Number.isNaN(parsed.getTime())) {
    validated = ` — validée le ${parsed.toLocaleDateString('fr-CH')}`;
  }
  const header = `Validation ${version}${validated}`;
  const refused = Number(set.refusedCount);
  const mentions = [];
  if (Number.isSafeInteger(refused) && refused > 0) {
    mentions.push(`${refused} valeur${refused > 1 ? 's' : ''} écartée${refused > 1 ? 's' : ''} par Anissa`);
  }
  if (ecartesParDestination > 0) {
    mentions.push(`${ecartesParDestination} valeur${ecartesParDestination > 1 ? 's' : ''} validée${ecartesParDestination > 1 ? 's' : ''} mais destinée${ecartesParDestination > 1 ? 's' : ''} au contexte, à vérification ou à l’avis médical`);
  }
  const completeness = mentions.length > 0 ? `\n  (${mentions.join(' ; ')} — non transmis ici)` : '';
  return `${header} :\n${lines.join('\n')}${completeness}`;
}

/**
 * Construit le bloc « RÉSULTATS DE LABORATOIRE VALIDÉS ».
 * @param {Array|null|undefined} sets - jeux issus de manualLabValidatedResultsClient
 * @returns {string} bloc prêt à concaténer, ou '' si rien.
 */
export function buildValidatedLabResultsBlockFr(sets) {
  const list = Array.isArray(sets) ? sets : [];
  const blocks = list.map(formatValidatedResultSet).filter(Boolean);
  if (blocks.length === 0) return '';
  return [
    'RÉSULTATS DE LABORATOIRE VALIDÉS PAR ANISSA :',
    'Ces valeurs ont été relues une par une par Anissa, qui les a acceptées ou corrigées, PUIS explicitement destinées au programme alimentaire. Les valeurs qu’elle a rangées ailleurs ne figurent pas ici. Elles servent de CONTEXTE pour orienter les propositions nutritionnelles.',
    ...blocks,
    "CADRE D'UTILISATION DE CES RÉSULTATS :",
    '- Ne pose aucun diagnostic et ne nomme aucune pathologie à partir de ces valeurs.',
    '- Ne qualifie aucune valeur de « normale », « anormale » ou « pathologique ».',
    "- N'indique aucun dosage de complément ni posologie à visée médicale.",
    '- Ne présente aucune interprétation comme certaine : formule des orientations, pas des conclusions.',
    "- Utilise ces valeurs uniquement pour orienter des choix alimentaires, et laisse la lecture clinique à Anissa.",
  ].join('\n');
}

// ─── BLOC SÉCURITÉ CLINIQUE (P0.1 + P0.2, remède 2026-06-10) ────────
//
// V97.64 : les contraintes maternelles (MATERNAL_SAFETY_FR) vivent désormais
// dans src/services/clinicalMaternalSafety.js (source unique FR+EN, DB-éditable
// + fallback hardcode validé). Consommées ici via getActiveMaternalSafetyEntry.

/**
 * Contraintes « profil à risque » par flag manuel praticienne (V97.58, volet
 * TCA de l'audit P4.7 — 2026-07-02). Flags résolus par resolveRiskProfileFlags
 * (registre : src/services/riskProfileFlags.js) ; posés UNIQUEMENT par la
 * praticienne dans la fiche (jugement clinique, pas de détection auto).
 *
 * ✅ CONTENU CLINIQUE VALIDÉ ANISSA — 2 JUILLET 2026 (réponses au doc
 * VALIDATION-CLINIQUE-ANISSA-V2-SESSION-JUILLET-2026.md, point 1) : base V1
 * validée + ses ajouts TCA (3 phrases), cancer (1 phrase interactions),
 * hépatique (reformulation protéines) et chirurgie (2 phrases cicatrisation),
 * intégrés tels que rédigés par elle. Formulations SCELLÉES — même rituel que
 * le P0 maternel du 12 juin. Toute modification repasse par Anissa.
 *
 * Donnée déclarative isolée (pattern MATERNAL_SAFETY_FR) : éditable, destinée
 * à migrer vers le registre Supabase. Injectée sur le chemin TOUJOURS ACTIF.
 */
const RISK_PROFILE_SAFETY_FR = {
  tca: {
    label: 'PROFIL TCA (suspecté ou déclaré)',
    rules: [
      'Aucune restriction calorique, aucun déficit chiffré, aucun objectif de perte de poids.',
      'Ne jamais chiffrer calories ni portions au gramme ; raisonner en repères qualitatifs (assiette, faim/satiété).',
      "Aucun aliment présenté comme « interdit », « à bannir » ou « à compenser » (hors allergènes médicaux).",
      'Ne jamais encourager le contrôle compulsif (pesée quotidienne, comptage systématique des calories, comportements compensatoires).',
      "Favoriser une approche basée sur les sensations alimentaires, la régularité des repas et la relation apaisée avec l'alimentation.",
      "Toute perte de poids éventuelle n'est jamais un objectif prioritaire dans ce contexte.",
      'Ton neutre et non culpabilisant ; ne jamais relier alimentation ou poids à la valeur personnelle.',
      'Plan à faire valider par Anissa avant toute remise à la cliente.',
    ],
  },
  cancer_actif: {
    label: 'CANCER EN TRAITEMENT ACTIF',
    rules: [
      "Aucune supplémentation — en particulier antioxydants à forte dose — sans validation explicite de l'équipe oncologique transmise à Anissa.",
      "Toute supplémentation doit être discutée avec l'équipe médicale en charge, certaines molécules pouvant interagir avec les traitements.",
      'Pas de jeûne ni de restriction calorique.',
      'Priorité au maintien du poids et des apports protéiques.',
      'Plan à faire valider par Anissa avant toute remise à la cliente.',
    ],
  },
  insuffisance_hepatique: {
    label: 'INSUFFISANCE HÉPATIQUE',
    rules: [
      'Aucune phytothérapie ni complément à risque hépatique (prudence maximale par défaut).',
      'Adapter les apports protéiques au contexte clinique et au stade de la maladie, sans augmentation systématique.',
      'Plan à faire valider par Anissa avant toute remise à la cliente.',
    ],
  },
  // chirurgie_recente : contraintes injectées mais SANS blocage de sortie
  // (comportement B, arbitrage Anissa 2026-07-02 — cf. riskProfileFlags.js).
  chirurgie_recente: {
    label: 'CHIRURGIE RÉCENTE (moins de 3 mois)',
    rules: [
      'Pas de jeûne ni de restriction calorique pendant la cicatrisation.',
      'Donner la priorité aux apports énergétiques et protéiques favorisant la cicatrisation.',
      'Éviter toute stratégie nutritionnelle pouvant ralentir la récupération.',
      'Prudence sur tout complément pouvant interférer avec la coagulation ou la cicatrisation.',
      'Plan à faire valider par Anissa avant toute remise à la cliente.',
    ],
  },
};

// ─── RESTRICTIONS ALIMENTAIRES (fondation anamnèse V1, 2026-06-13) ──────
//
// Phrases d'injection FR. DISTINCTES du gate allergène (l'allergie est un gate
// MÉDICAL bloquant ; la restriction est COMPORTEMENTALE/préventive). RESTRICTIONS
// PERMANENTES uniquement (deux natures, deux traitements, cf. restrictions.fr.js) :
//   religieux  → ne jamais proposer volontairement un aliment incompatible
//   preference → best-effort, sans sur-restreindre
//
// Le Ramadan N'EST PAS ici : c'est un état TEMPORAIRE de timing, injecté
// séparément par buildRamadanLineFr conditionné à isRamadanActive (champ dédié
// ramadanActif, activé manuellement par la praticienne). Leçon : ne pas fondre
// une nature différente dans un traitement unique.
//
// Formulation validée Anissa — 13 juin 2026 (scellée, même rituel que le P0
// grossesse). Anti-sur-restriction : chaque phrase cadre la contrainte SANS
// pousser l'IA à éliminer des aliments compatibles par excès de zèle. Gradation
// voulue par Anissa : religieux = absolu (« ne jamais proposer ») ; préférence =
// fort mais souple (« respecter au mieux et éviter de proposer »).
function buildRestrictionsLinesFr(form) {
  const r = resolveRestrictions(form);
  if (!hasRestrictions(r)) return [];
  const lines = [];

  if (r.religieux.length > 0) {
    const labels = r.religieux.map((x) => x.label).join(', ');
    lines.push(`- RESTRICTIONS RELIGIEUSES déclarées : ${labels}. Ne jamais proposer volontairement un aliment incompatible avec ces restrictions — dans aucun menu, recette, liste de courses ou suggestion de complément. Ces restrictions ne limitent QUE les aliments incompatibles : tous les aliments compatibles restent pleinement utilisables (ne pas sur-restreindre).`);
  }

  if (r.preference.length > 0 || r.autreText) {
    const labels = r.preference.map((x) => x.label);
    if (r.autreText) labels.push(r.autreText);
    lines.push(`- RESTRICTIONS ALIMENTAIRES (préférence) déclarées : ${labels.join(', ')}. Respecter ces choix autant que possible et éviter de proposer des aliments incompatibles. Rester dans le périmètre normal du régime, sans restreindre au-delà. Un régime végan n'exclut QUE les produits animaux, pas les aliments végétaux compatibles.`);
  }

  return lines;
}

// Ramadan (état temporaire de TIMING, activé manuellement via ramadanActif).
// Distinct des restrictions permanentes : pas une exclusion d'aliment mais une
// contrainte de structure des repas. Phrase fusionnant les deux validations
// Anissa (fenêtre alimentaire + REDISTRIBUTION active des apports sur la fenêtre,
// pas seulement « ne pas réduire »). Formulation validée Anissa — 13 juin 2026.
function buildRamadanLineFr(form) {
  if (!isRamadanActive(form)) return '';
  return `- RAMADAN en cours (jeûne observé) — CONTRAINTES DE TIMING : structurer les repas dans la fenêtre autorisée (avant l'aube et après le coucher du soleil) ; redistribuer les apports sur cette fenêtre sans diminuer les apports énergétiques quotidiens ; renforcer la vigilance hydratation ; éviter les stratégies hypocaloriques agressives ; ne jamais ajouter de jeûne supplémentaire par-dessus le jeûne du Ramadan.`;
}

// Compléments déjà pris (anti-doublon). Formulation validée Anissa —
// 13 juin 2026. Injecté dans le contexte IA (pas affichage seul) :
// sans cette ligne, l'IA recommande des compléments en doublon de ce que la
// cliente prend déjà (trou structurel constaté : form.supplements n'atteignait
// aucun prompt avant V1).
function buildComplementsActuelsLineFr(form) {
  const liste = formatComplementsActuels(form);
  if (!liste) return '';
  return `- COMPLÉMENTS déjà pris par la cliente : ${liste}. En tenir compte : ne pas recommander de doublon de ce qui est déjà pris, et signaler à Anissa toute interaction possible plutôt que d'agir dessus.`;
}

// Ouverture aux compléments (enum 3 niveaux). Directive SOUPLE (préférence, pas
// contrainte de sécurité) → injectée hors bloc sécurité, dans buildSystemPromptFr.
// Contenu validé Anissa sur le fond (3 libellés) ; formulation de la directive
// validée Anissa — 13 juin 2026.
const OUVERTURE_DIRECTIVE_FR = Object.freeze({
  eviter: `OUVERTURE AUX COMPLÉMENTS : la cliente préfère éviter les compléments (« ${OUVERTURE_COMPLEMENTS.eviter} »). Privilégier les apports par l'alimentation ; ne suggérer un complément que s'il est réellement essentiel, en le justifiant.`,
  si_necessaire: `OUVERTURE AUX COMPLÉMENTS : la cliente est ouverte si nécessaire (« ${OUVERTURE_COMPLEMENTS.si_necessaire} »). Proposer des compléments uniquement lorsqu'ils apportent un bénéfice clair.`,
  a_laise: `OUVERTURE AUX COMPLÉMENTS : la cliente est à l'aise avec les compléments (« ${OUVERTURE_COMPLEMENTS.a_laise} »). Les propositions de compléments pertinents sont les bienvenues.`,
});

/**
 * Directive d'ouverture aux compléments, à injecter dans le system prompt FR.
 * Renvoie '' si non renseignée. Pure, fail-safe.
 * @param {object|null|undefined} form
 * @returns {string}
 */
export function buildOuvertureComplementsDirectiveFr(form) {
  const level = resolveOuvertureComplements(form);
  return level ? OUVERTURE_DIRECTIVE_FR[level] : '';
}

// ─── NOTES PRATICIENNE — questions/réponses du RDV anamnèse (2026-07-07) ──
//
// Bloc « Questions du RDV » saisi par Anissa dans l'étape Onboarding (SaaS,
// StepAnamnesis). Structure : form.rdv_questions = [{ id, text, source, included,
// answer }]. On n'injecte QUE les entrées included:true dont answer est non vide.
//
// Nature : CONTEXTE de la praticienne, PAS des prescriptions ni des contraintes
// de sécurité → injecté hors bloc sécurité, sans force impérative. Aucune décision
// clinique n'est codée ici : ce sont les mots d'Anissa, transmis tels quels.
// L'IA les lit comme des observations pour orienter le plan, jamais comme un ordre.
//
// Garde-fous stricts : ce bloc ne touche jamais aux pistes d'analyses
// (suggested_analyses) — celles-ci restent en lecture seule dans le SaaS, à valider
// cliniquement, hors périmètre du plan auto. Fail-safe : renvoie '' si rien.
/**
 * Construit le bloc « NOTES PRATICIENNE » depuis form.rdv_questions.
 * @param {object|null|undefined} form
 * @returns {string} bloc texte prêt à concaténer, ou '' si aucune réponse.
 */
export function buildRdvNotesBlockFr(form) {
  if (!form || typeof form !== 'object') return '';
  const questions = Array.isArray(form.rdv_questions) ? form.rdv_questions : [];
  const lines = questions
    .filter((q) => q && q.included === true && String(q.answer ?? '').trim() !== '')
    .map((q) => `- ${String(q.text ?? '').trim()} : ${String(q.answer).trim()}`);
  if (lines.length === 0) return '';
  return [
    "NOTES D'ANISSA — Questions/réponses du RDV anamnèse (contexte, pas des prescriptions) :",
    ...lines,
  ].join('\n');
}

/**
 * Construit le bloc « SÉCURITÉ CLINIQUE » injecté dans le system prompt de
 * génération de plan. SOURCE UNIQUE des données de sécurité (allergènes,
 * intolérances, traitements/médicaments).
 *
 * Dérivé directement de `form` — et non de `clinicalContext` — parce que les
 * trois builders de prompt (composer / classique `buildSystemPromptFr` / legacy
 * `NutritionConsultation`) reçoivent tous `form`, mais un seul consomme
 * `clinicalContext`. En dérivant de form et en appelant cette fonction depuis
 * chaque builder, on garantit la présence du bloc sur TOUS les chemins
 * (cf. correction d'architecture du remède, 2026-06-10).
 *
 * Additif strict : n'enlève aucun canal user-message existant (le retrait des
 * doublons est P1, fait une fois ce canal prouvé sur les trois chemins).
 * Fail-safe : retourne '' si aucune donnée de sécurité (ne throw jamais).
 *
 * @param {object|null|undefined} form - anamnèse cliente
 * @returns {string} bloc texte prêt à concaténer, ou '' si rien.
 */
export function buildSafetyBlockFr(form) {
  if (!form || typeof form !== 'object') return '';
  const allergies = String(form.allergies ?? '').trim();
  const intolerances = String(form.alimentsEvites ?? '').trim();
  // `||` (et non `??`) pour que l'alias medicaments prenne le relais quand
  // traitements est une chaîne vide (et pas seulement null/undefined).
  const medications = formatActiveTreatments(form, { includeMoment: true });

  // P0 maternel (Module 1) — calculé AVANT la garde précoce : une cliente
  // enceinte/allaitante SANS allergie ni médicament doit quand même recevoir
  // ses contraintes. V97.59 : source = resolveMaternalStateExtended (structuré
  // in-app + legacy ET filet texte libre — le même que le composer). Une
  // grossesse signalée seulement en texte libre déclenche désormais le bloc.
  const maternal = resolveMaternalStateExtended(form);
  const maternalRules = getActiveMaternalSafetyEntry(maternal, 'fr');

  // Profils à risque (V97.58) — même logique que le P0 maternel : calculés
  // AVANT la garde précoce, un flag seul (sans allergie ni médicament) doit
  // suffire à produire un bloc sécurité.
  const riskFlags = resolveRiskProfileFlags(form);
  const riskRules = riskFlags.map((id) => RISK_PROFILE_SAFETY_FR[id]).filter(Boolean);

  // Fondation anamnèse V1 : restrictions (comportemental, distinct du gate
  // allergène médical) + compléments déjà pris (anti-doublon, contexte IA).
  const restrictionLines = buildRestrictionsLinesFr(form);
  const ramadanLine = buildRamadanLineFr(form);
  const complementsLine = buildComplementsActuelsLineFr(form);

  // 2026-07-20 (demande Benoit/Anissa) — présentation sans chiffres :
  // préférence posée par la PRATICIENNE (cliente sensible aux chiffres :
  // poids, grammages, calories). Injectée ICI, au point de passage unique,
  // pour couvrir composer + recettes + alternatives sans dépendre de la
  // mémoire d'Anissa à chaque génération. Règle de PRÉSENTATION additive-
  // restrictive : hors registre des profils à risque (scellé, clairance) —
  // aucun blocage, aucune décision clinique.
  const sansChiffres = form.presentationSansChiffres === true;

  if (!allergies && !intolerances && !medications && !maternalRules
      && riskRules.length === 0 && !sansChiffres
      && restrictionLines.length === 0 && !ramadanLine && !complementsLine) return '';

  const lines = [
    'SÉCURITÉ CLINIQUE — CONTRAINTES ABSOLUES (priorité sur toute autre consigne de ce prompt) :',
  ];
  if (maternalRules) {
    lines.push(`- ${maternalRules.label} — CONTRAINTES ABSOLUES : ${maternalRules.rules.join(' ')}`);
  }
  for (const rr of riskRules) {
    lines.push(`- ${rr.label} — CONTRAINTES ABSOLUES : ${rr.rules.join(' ')}`);
  }
  if (allergies) {
    // Micro-correctif P0.1 (2026-06-10) : la traduction EN (P0.5) a révélé que
    // le titre FR portait moins de force que le corps — « À EXCLURE
    // STRICTEMENT » n'apparaissait que dans la consigne, pas dans l'intitulé.
    // Aligné sur l'EN (« STRICTLY EXCLUDE ») pour que l'injonction soit lisible
    // dès le titre de ligne.
    lines.push(`- ALLERGÈNES déclarés — À EXCLURE STRICTEMENT : ${allergies}. N'inclure AUCUN de ces aliments — ni dérivé, ni trace — dans aucun menu, recette, liste de courses ou suggestion de complément. Si tu nommes un allergène dans le plan, uniquement sous une forme d'exclusion courte et explicite (« sans nickel », « éviter le nickel », « allergie au nickel ») et jamais à côté d'un aliment servi ; sinon décris directement les aliments de remplacement.`);
  }
  if (intolerances) {
    lines.push(`- Intolérances / aliments à éviter : ${intolerances}. Exclure de toutes les propositions.`);
  }
  if (medications) {
    lines.push(`- Traitements / médicaments en cours : ${medications}. Tenir compte des interactions aliment-/complément-médicament connues (ex. millepertuis ↔ antidépresseurs, vitamine K ↔ anticoagulants, pamplemousse ↔ statines). Ne jamais suggérer d'arrêter, de modifier ni de remplacer un traitement ; en cas d'interaction possible, le signaler à Anissa plutôt que d'agir dessus.`);
  }
  // Restrictions + compléments en fin de bloc : APRÈS le gate allergène, pour ne
  // pas diluer la priorité de l'exclusion stricte des allergènes.
  for (const rl of restrictionLines) lines.push(rl);
  if (ramadanLine) lines.push(ramadanLine);
  if (complementsLine) lines.push(complementsLine);
  if (sansChiffres) {
    // Exception relevée par clinique-guard (2026-07-20) : certains profils
    // imposent des quantités CLINIQUES (grammage protéique néphropathie,
    // kcal grossesse/allaitement). Défaut conservateur : le clinique prime
    // toujours sur la présentation. Combinaison à arbitrer par Anissa.
    lines.push("- PRÉSENTATION SANS CHIFFRES (choix d'Anissa) : exprime toutes les portions en unités familières (une assiette, une poignée, une tranche, un filet, un demi-avocat…). N'écris JAMAIS de grammes, kilogrammes, millilitres, calories, kcal ou pourcentages dans les repas, recettes, alternatives, astuces, listes de courses ou conseils. EXCEPTION : si une contrainte clinique de ce bloc impose une quantité chiffrée précise (ex. grammage protéique, apport calorique), cette contrainte clinique PRIME et le chiffre doit apparaître.");
  }
  return lines.join('\n');
}

// ─── Helpers de formatting (tolérants aux conventions de noms) ──────

/**
 * Sélectionne la première valeur non-vide parmi plusieurs clés candidates.
 * Renvoie '' si aucune valeur trouvée.
 */
function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return '';
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
}

/**
 * Formate un test sélectionné : "Nom — laboratoire — catégorie — prix CHF".
 * Tolère : test.name | test.display_name, test.lab | test.source_lab,
 * test.category, test.price_chf | test.cost_anissa_chf.
 */
export function formatTest(test) {
  if (!test || typeof test !== 'object') return '';
  const name = pick(test, 'name', 'display_name');
  if (!name) return '';
  const lab = pick(test, 'lab', 'source_lab');
  const category = pick(test, 'category');
  const price = pick(test, 'price_chf', 'cost_anissa_chf');
  const parts = [name];
  if (lab) parts.push(String(lab));
  if (category) parts.push(String(category));
  if (price !== '' && price !== null && !Number.isNaN(Number(price))) {
    parts.push(`${price} CHF`);
  }
  return `- ${parts.join(' — ')}`;
}

/**
 * Formate un marqueur attendu : "Nom — unité — axe clinique".
 * Tolère : marker.label | marker.name, marker.unit, marker.clinical_axis | marker.axis.
 */
export function formatMarker(marker) {
  if (!marker || typeof marker !== 'object') return '';
  const label = pick(marker, 'label', 'name');
  if (!label) return '';
  const unit = pick(marker, 'unit');
  const axis = pick(marker, 'clinical_axis', 'axis');
  const parts = [label];
  if (unit) parts.push(String(unit));
  if (axis) parts.push(String(axis));
  return `- ${parts.join(' — ')}`;
}

/**
 * Formate un résultat saisi : "Marqueur : valeur unité — note Anissa".
 * Tolère : result.label | result.marker_label | result.test_name,
 * result.value, result.unit, result.synthesis | result.note_anissa.
 */
export function formatResult(result) {
  if (!result || typeof result !== 'object') return '';
  const label = pick(result, 'label', 'marker_label', 'test_name');
  const value = pick(result, 'value');
  if (!label || value === '') return '';
  const unit = pick(result, 'unit');
  const note = pick(result, 'synthesis', 'note_anissa');
  const valueWithUnit = unit ? `${value} ${unit}` : `${value}`;
  let line = `- ${label} : ${valueWithUnit}`;
  if (note) line += ` — ${note}`;
  return line;
}

/**
 * Formate un signal clinique : "Label — priorité X — confiance Y — source: a, b".
 * Tolère : signal.label | signal.id, signal.priority | signal.priority_score,
 * signal.confidence, signal.source_markers (array).
 */
export function formatSignal(signal) {
  if (!signal || typeof signal !== 'object') return '';
  const label = pick(signal, 'label', 'id');
  if (!label) return '';
  const priority = pick(signal, 'priority', 'priority_score');
  const confidence = pick(signal, 'confidence');
  const sources = Array.isArray(signal.source_markers) ? signal.source_markers.filter(Boolean) : [];
  const parts = [label];
  if (priority !== '') parts.push(`priorité ${priority}`);
  if (confidence) parts.push(`confiance ${confidence}`);
  if (sources.length > 0) parts.push(`source : ${sources.join(', ')}`);
  return `- ${parts.join(' — ')}`;
}

/**
 * Formate la phase microbiome active en bloc multi-ligne.
 * Renvoie '' si stage absent ou sans label/id.
 *
 * V3.F — wording prudent : "Phase probable" plutôt que "Phase".
 * Affiche confidence + reasons + override quand fournis (champs V3.F),
 * rétro-compat avec callers qui passent seulement label/goal (V1).
 */
export function formatMicrobiomeStage(stage) {
  if (!stage || typeof stage !== 'object') return '';
  const label = pick(stage, 'label', 'name');
  const id = pick(stage, 'id');

  // V3.F : si pas de phase tranchée mais des raisons audit présentes,
  // on émet quand même un bloc "Pas de phase tranchée — pistes" pour
  // que l'IA voit l'analyse partielle (transparent).
  const reasons = Array.isArray(stage.reasons) ? stage.reasons.filter(Boolean) : [];
  if (!label && !id) {
    if (reasons.length === 0) return '';
    const lines = ['- Phase microbiome : non tranchée (vigilance, pistes partielles)'];
    lines.push(`- Indices observés : ${reasons.join(' ; ')}`);
    return lines.join('\n');
  }

  const overridden = stage.overridden_by_practitioner === true;
  const confidence = pick(stage, 'confidence');
  const lines = [];

  // V3.F : wording suggestif, jamais prescriptif.
  if (overridden) {
    lines.push(`- Phase retenue par Anissa : ${label || id}`);
    const overrideReason = pick(stage, 'override_reason');
    if (overrideReason) lines.push(`- Motif indiqué par Anissa : ${overrideReason}`);
  } else {
    const cf = confidence ? ` (confiance ${confidence})` : '';
    lines.push(`- Phase probable : ${label || id}${cf}`);
  }

  const goal = pick(stage, 'goal', 'objective');
  if (goal) lines.push(`- Objectif : ${goal}`);

  if (reasons.length > 0) {
    lines.push(`- Raisons : ${reasons.join(' ; ')}`);
  }

  const targetMarkers = Array.isArray(stage.target_markers) ? stage.target_markers.filter(Boolean) : [];
  if (targetMarkers.length > 0) {
    lines.push(`- Marqueurs à surveiller : ${targetMarkers.join(', ')}`);
  }

  const allowed = Array.isArray(stage.allowed_interventions) ? stage.allowed_interventions.filter(Boolean) : [];
  if (allowed.length > 0) lines.push(`- Autorisé : ${allowed.join(', ')}`);

  const blocked = Array.isArray(stage.blocked_interventions) ? stage.blocked_interventions.filter(Boolean) : [];
  if (blocked.length > 0) lines.push(`- À éviter : ${blocked.join(', ')}`);

  return lines.join('\n');
}

// ─── Builder principal ──────────────────────────────────────────────

/**
 * Construit le bloc clinique structuré injecté dans le system prompt.
 *
 * @param {object | null | undefined} clinicalContext
 * @returns {string} Texte brut prêt à concaténer dans le prompt, ou '' si vide.
 */
export function buildClinicalContextBlockFr(clinicalContext) {
  // Garde-fou principal : ne throw jamais, retourne '' si rien à injecter.
  if (isEmptyClinicalContext(clinicalContext)) return '';

  const ctx = clinicalContext;
  const blocks = [];

  // ─── ANALYSES SÉLECTIONNÉES ─────────────────────────────────────
  const selectedTests = Array.isArray(ctx.selectedTests) ? ctx.selectedTests : [];
  const testLines = selectedTests.map(formatTest).filter(Boolean);
  if (testLines.length > 0) {
    blocks.push(`ANALYSES SÉLECTIONNÉES :\n${testLines.join('\n')}`);
  }

  // ─── MARQUEURS ATTENDUS ─────────────────────────────────────────
  const expectedMarkers = Array.isArray(ctx.expectedMarkers) ? ctx.expectedMarkers : [];
  const markerLines = expectedMarkers.map(formatMarker).filter(Boolean);
  if (markerLines.length > 0) {
    blocks.push(`MARQUEURS ATTENDUS :\n${markerLines.join('\n')}`);
  }

  // ─── RÉSULTATS DISPONIBLES ──────────────────────────────────────
  // Garde-fou : si selectedTests vide mais enteredResults présent, préciser
  // qu'il s'agit d'analyses externes ou non reliées à un plan.
  const enteredResults = Array.isArray(ctx.enteredResults) ? ctx.enteredResults : [];
  const resultLines = enteredResults.map(formatResult).filter(Boolean);
  if (resultLines.length > 0) {
    const header = (selectedTests.length === 0)
      ? 'RÉSULTATS DISPONIBLES (analyses externes ou non reliées à un plan) :'
      : 'RÉSULTATS DISPONIBLES :';
    blocks.push(`${header}\n${resultLines.join('\n')}`);
  }

  // ─── RÉSULTATS VALIDÉS PAR LA PRATICIENNE (V97.177) ─────────────
  // Module séparé, placé APRÈS les résultats saisis libres : ces valeurs-ci
  // ont été relues une par une, elles priment donc en lisibilité pour l'IA.
  const validatedBlock = buildValidatedLabResultsBlockFr(ctx.validatedLabResults);
  if (validatedBlock) blocks.push(validatedBlock);

  // ─── SIGNAUX CLINIQUES PRIORITAIRES ─────────────────────────────
  const clinicalSignals = Array.isArray(ctx.clinicalSignals) ? ctx.clinicalSignals : [];
  const signalLines = clinicalSignals.map(formatSignal).filter(Boolean);
  if (signalLines.length > 0) {
    blocks.push(`SIGNAUX CLINIQUES PRIORITAIRES :\n${signalLines.join('\n')}`);
  }

  // ─── PHASE MICROBIOME ACTIVE ────────────────────────────────────
  const stageBlock = formatMicrobiomeStage(ctx.microbiomeStage);
  if (stageBlock) {
    blocks.push(`PHASE MICROBIOME ACTIVE :\n${stageBlock}`);
  }

  // ─── MODULES IA SPÉCIFIQUES (optionnel V1, prévu pour V2+) ─────
  const promptModules = Array.isArray(ctx.promptModules) ? ctx.promptModules.filter(Boolean) : [];
  if (promptModules.length > 0) {
    blocks.push(`MODULES CLINIQUES MOBILISÉS :\n${promptModules.map((m) => `- ${m}`).join('\n')}`);
  }

  // ─── RÈGLES SAFETY SUPPLÉMENTAIRES (optionnel V1) ──────────────
  const safetyRules = Array.isArray(ctx.safetyRules) ? ctx.safetyRules.filter(Boolean) : [];
  if (safetyRules.length > 0) {
    blocks.push(`RÈGLES DE SÉCURITÉ SPÉCIFIQUES :\n${safetyRules.map((r) => `- ${r}`).join('\n')}`);
  }

  // Garde-fou : si tout filtre laisse blocks vide après l'isEmptyClinicalContext
  // (entrées présentes mais toutes mal formées), on n'injecte rien.
  if (blocks.length === 0) return '';

  // ─── RÈGLES D'INTERPRÉTATION (toujours injectées si bloc non vide) ──
  const interpretationRules = `RÈGLES D'INTERPRÉTATION :
- Ne jamais interpréter une analyse non réalisée.
- Ne jamais conclure sur un marqueur absent.
- Ne pas poser de diagnostic.
- Utiliser les résultats comme éléments d'orientation nutritionnelle.
- Limiter la synthèse à 3 priorités principales.
- Si les résultats sont incomplets, le préciser clairement.`;

  return `CONTEXTE CLINIQUE STRUCTURÉ :

${blocks.join('\n\n')}

${interpretationRules}`;
}
